import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useApp } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";

export default function PaymentResult({ cancelled }) {
  const { L } = useApp();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const paypal = params.get("provider") === "paypal";
  const sid = paypal ? params.get("subscription_id") || params.get("token") : params.get("session_id");
  const [state, setState] = useState(cancelled || !sid ? "cancelled" : "checking");
  const [kind, setKind] = useState("plan");
  useEffect(() => {
    if (state !== "checking") return;
    let n = 0, t;
    const poll = async () => {
      try {
        const { data } = paypal ? await api.get("/payments/paypal/status", { params: { ref: sid } }) : await api.get(`/payments/status/${sid}`);
        setKind(data.kind);
        if (data.payment_status === "paid") { setState("paid"); refresh?.(); return; }
        if (["expired", "failed"].includes(data.payment_status)) return setState("failed");
      } catch { /* retry */ }
      if (++n < 15) t = setTimeout(poll, 2000); else setState("timeout");
    };
    poll();
    return () => clearTimeout(t);
  }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps
  const view = {
    checking: [Loader2, "text-violet-600 animate-spin", L("Mengesahkan pembayaran…", "Confirming payment…"), L("Sila tunggu sebentar.", "Please wait a moment.")],
    paid: [CheckCircle2, "text-emerald-600", L("Pembayaran berjaya!", "Payment successful!"), kind === "topup" ? L("Kuota tambahan telah ditambah untuk kitaran bil semasa.", "Extra quota has been added for the current billing cycle.") : L("Pelan anda kini aktif. Resit dihantar ke emel anda oleh Stripe.", "Your plan is now active. Stripe has emailed your receipt.")],
    failed: [XCircle, "text-red-600", L("Pembayaran gagal", "Payment failed"), L("Tiada caj dikenakan. Sila cuba lagi.", "No charge was made. Please try again.")],
    cancelled: [XCircle, "text-slate-400", L("Pembayaran dibatalkan", "Payment cancelled"), L("Tiada caj dikenakan. Anda boleh cuba semula bila-bila masa.", "No charge was made. You can try again anytime.")],
    timeout: [Loader2, "text-amber-500", L("Masih memproses", "Still processing"), L("Pembayaran sedang disahkan. Semak halaman Langganan sebentar lagi.", "Payment is being confirmed. Check the Billing page shortly.")],
  }[state];
  const [Icon, cls, title, desc] = view;
  return (
    <div className="mx-auto grid min-h-[70vh] max-w-lg place-items-center px-4" data-testid="payment-result">
      <div className="w-full rounded-3xl border border-slate-200 bg-white p-8 text-center">
        <Icon size={48} className={`mx-auto ${cls}`} />
        <h1 className="mt-4 text-2xl font-extrabold" data-testid="payment-result-title" data-state={state}>{title}</h1>
        <p className="mt-2 text-sm text-slate-500">{desc}</p>
        <Button asChild className="mt-6 rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="payment-back-btn"><Link to="/app/billing">{L("Ke Langganan & Penggunaan", "Go to Billing & Usage")}</Link></Button>
      </div>
    </div>
  );
}
